import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AllInbox,
  VerifiedUser,
  LocationOn,
  ExpandMore,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const SEARCH_STORAGE_KEY = "travel_search";
const SELECTED_SOURCE_SEARCH = "selected_source_search";
const SELECTED_DESTINATION_SEARCH = "selected_destination_search";

export default function Search({ initialValues = {} }) {
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 850);
  const [mobileModal, setMobileModal] = useState(null); // null ou "origem" ou "destino" ou "encomenda"

  const [origem, setOrigem] = useState(initialValues.origem || "");
  const [destino, setDestino] = useState(initialValues.destino || "");
  const [origemSelecionada, setOrigemSelecionada] = useState(false);
  const [destinoSelecionado, setDestinoSelecionado] = useState(false);

  const [erroOrigem, setErroOrigem] = useState(false);
  const [erroDestino, setErroDestino] = useState(false);
  const [encomenda, setEncomenda] = useState(initialValues.encomenda || "");
  const [openEnvelopeWarning, setOpenEnvelopeWarning] = useState(false);

  const [openSeguroModal, setOpenSeguroModal] = useState(false);
  const [valorSeguro, setValorSeguro] = useState("");
  const [erroSeguro, setErroSeguro] = useState(false);
  const [erroEncomenda, setErroEncomenda] = useState(false);
  const [valorSeguroInput, setValorSeguroInput] = useState("");

  const [openPanel, setOpenPanel] = useState(null);
  const [originPredictions, setOriginPredictions] = useState([]);
  const [destPredictions, setDestPredictions] = useState([]);
  const [loadingOrigin, setLoadingOrigin] = useState(false);
  const [loadingDest, setLoadingDest] = useState(false);

  const mobileInputRef = useRef(null);

  const serviceRef = useRef(null);
  const originTimerRef = useRef(null);
  const destTimerRef = useRef(null);

  const origemWrapRef = useRef(null);
  const destinoWrapRef = useRef(null);
  const encomendaWrapRef = useRef(null);

  const [origemMeta, setOrigemMeta] = useState(null);
  const [destinoMeta, setDestinoMeta] = useState(null);

  useEffect(() => {
    if (mobileModal === "origem" || mobileModal === "destino") {
      const timer = setTimeout(() => {
        mobileInputRef.current?.focus();

        const input = mobileInputRef.current;
        if (input) {
          const length = input.value.length;
          input.setSelectionRange(length, length);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [mobileModal]);

  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth <= 850);

    window.addEventListener("resize", resize);

    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    setOrigem(initialValues.origem || "");
    setDestino(initialValues.destino || "");
    setEncomenda(initialValues.encomenda || "");
    setValorSeguro(initialValues.seguro || "");

    setOrigemSelecionada(!!initialValues.origem);
    setDestinoSelecionado(!!initialValues.destino);

    setOrigemMeta(
      initialValues.origem_lat && initialValues.origem_lng
        ? {
            lat: Number(initialValues.origem_lat),
            lng: Number(initialValues.origem_lng),
          }
        : null,
    );

    setDestinoMeta(
      initialValues.destino_lat && initialValues.destino_lng
        ? {
            lat: Number(initialValues.destino_lat),
            lng: Number(initialValues.destino_lng),
          }
        : null,
    );
  }, [
    initialValues.origem,
    initialValues.destino,
    initialValues.encomenda,
    initialValues.origem_lat,
    initialValues.origem_lng,
    initialValues.destino_lat,
    initialValues.destino_lng,
  ]);

  const formatarMoeda = (valor) => {
    const numeros = valor.replace(/\D/g, "");
    if (!numeros) return "";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(numeros) / 100);
  };

  const obterDetalhesLugar = (placeId) => {
    return new Promise((resolve, reject) => {
      if (!window.google?.maps?.places) {
        reject(new Error("Google Maps não carregou."));
        return;
      }

      const service = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );

      service.getDetails(
        {
          placeId,
          fields: ["geometry", "formatted_address", "name"],
        },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            resolve(place);
            return;
          }

          reject(new Error("Não foi possível obter os detalhes do lugar."));
        },
      );
    });
  };

  const dedupeCities = (predictions = []) => {
    const seen = new Set();

    return predictions.filter((item) => {
      const city =
        item.structured_formatting?.main_text?.trim() ||
        item.description.split(",")[0]?.trim() ||
        "";

      const key = city.toLowerCase();

      if (!city || seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  };

  const encomendas = [
    "Pequeno, estilo envelope",
    "Caixa de Sapato/Sacola",
    "Mochila/Mala",
    "Mala/Caixa",
  ];

  useEffect(() => {
    sessionStorage.setItem(SELECTED_SOURCE_SEARCH, 0);
    sessionStorage.setItem(SELECTED_DESTINATION_SEARCH, 0);
  }, []);

  useEffect(() => {
    const waitForGoogle = () => {
      if (window.google?.maps?.places?.AutocompleteService) {
        serviceRef.current =
          new window.google.maps.places.AutocompleteService();
        return true;
      }
      return false;
    };

    if (waitForGoogle()) return;

    const interval = setInterval(() => {
      if (waitForGoogle()) {
        clearInterval(interval);
      }
    }, 200);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInside =
        origemWrapRef.current?.contains(event.target) ||
        destinoWrapRef.current?.contains(event.target) ||
        encomendaWrapRef.current?.contains(event.target);

      if (!clickedInside) {
        setOpenPanel(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!serviceRef.current) return;

    if (originTimerRef.current) clearTimeout(originTimerRef.current);

    if (!origem || openPanel !== "origem") {
      setOriginPredictions([]);
      setLoadingOrigin(false);
      return;
    }

    setLoadingOrigin(true);

    originTimerRef.current = setTimeout(() => {
      serviceRef.current.getPlacePredictions(
        {
          input: origem,
          componentRestrictions: { country: "br" },
          types: ["(cities)"],
        },
        (predictions, status) => {
          const ok =
            status === window.google?.maps?.places?.PlacesServiceStatus?.OK ||
            status === "OK";

          setOriginPredictions(
            dedupeCities(ok && Array.isArray(predictions) ? predictions : []),
          );
          setLoadingOrigin(false);
        },
      );
    }, 250);

    return () => {
      if (originTimerRef.current) clearTimeout(originTimerRef.current);
    };
  }, [origem, openPanel]);

  useEffect(() => {
    if (!serviceRef.current) return;

    if (destTimerRef.current) clearTimeout(destTimerRef.current);

    if (!destino || openPanel !== "destino") {
      setDestPredictions([]);
      setLoadingDest(false);
      return;
    }

    setLoadingDest(true);

    destTimerRef.current = setTimeout(() => {
      serviceRef.current.getPlacePredictions(
        {
          input: destino,
          componentRestrictions: { country: "br" },
          types: ["(cities)"],
        },
        (predictions, status) => {
          const ok =
            status === window.google?.maps?.places?.PlacesServiceStatus?.OK ||
            status === "OK";

          setDestPredictions(
            dedupeCities(ok && Array.isArray(predictions) ? predictions : []),
          );
          setLoadingDest(false);
        },
      );
    }, 250);

    return () => {
      if (destTimerRef.current) clearTimeout(destTimerRef.current);
    };
  }, [destino, openPanel]);

  const handleSearch = () => {
    let hasError = false;

    if (!origemSelecionada) {
      setErroOrigem(true);
      hasError = true;
    }

    if (!destinoSelecionado) {
      setErroDestino(true);
      hasError = true;
    }

    if (!encomenda.trim()) {
      setErroEncomenda(true);
      hasError = true;
    }

    if (!valorSeguro.trim()) {
      setErroSeguro(true);
      hasError = true;
    }

    if (hasError) return;

    const payload = {
      origem: origem.trim(),
      destino: destino.trim(),
      encomenda: encomenda.trim(),
      seguro: valorSeguro.trim(),
      origem_lat: origemMeta?.lat ?? "",
      origem_lng: origemMeta?.lng ?? "",
      destino_lat: destinoMeta?.lat ?? "",
      destino_lng: destinoMeta?.lng ?? "",
    };

    sessionStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(payload));
    navigate("/busca", { state: payload });
    window.location.reload();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const selectOrigin = async (prediction) => {
    console.log("prediction:", prediction);
    try {
      const place = await obterDetalhesLugar(prediction.place_id);

      console.log("place:", place);

      setOrigem(place.formatted_address || prediction.description);

      setOrigemMeta({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      });

      setOrigemSelecionada(true);
      setErroOrigem(false);
      setOriginPredictions([]);
      setOpenPanel(null);
      setMobileModal(null);
    } catch (err) {
      console.error(err);

      setOrigem(prediction.description);

      setOrigemSelecionada(true);
      setMobileModal(null);
      setOpenPanel(null);
    }
  };

  const selectDestino = async (prediction) => {
    const place = await obterDetalhesLugar(prediction.place_id);

    setDestino(place.formatted_address || prediction.description);

    setDestinoMeta({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    });

    setDestinoSelecionado(true);
    sessionStorage.setItem(SELECTED_DESTINATION_SEARCH, 1);
    setErroDestino(false);
    setDestPredictions([]);
    setOpenPanel(null);

    if (isMobile) {
      setMobileModal(null);
    }
  };

  const selectEncomenda = (value) => {
    setEncomenda(value);
    setErroEncomenda(false);
    setOpenPanel(null);
    setValorSeguro("");

    if (value === "Pequeno, estilo envelope") {
      setOpenEnvelopeWarning(true);
      setValorSeguro("R$ 0,00");
      setErroSeguro(false);
    }
  };

  return (
    <>
      <style>{`
        
        .search-bar { 
          margin: -20px auto;
          padding: 10px 15px;
          border-radius: 999px;
          background: #b5d5f3; 
          box-shadow: 5px 5px 8px 2px rgba(1, 9, 18, 0.1);
          display: flex;
          flex-wrap: nowrap;
          align-items: center;
          gap: 15px; 
        }

        .search-item {
          position: relative;
          background: #fff;
          color: #607086ff;
          padding: 0 12px;
          display: flex;
          border: 1px solid #11b9a3;
          align-items: center;
          gap: 8px; 
          border-radius: 999px;
          height: 40px;
          font-size: 13px;
          flex: 1 1 0;     
          min-width: 120px; 
          overflow: visible;
        }

        .search-item input,
        .search-item button.field-btn {
          width: 100%;
          min-width: 0; 
          color: #555;
          padding: 0px;
          background: transparent;
          border: none;
          outline: none;
          text-align: left;
          font: inherit;
        }

        .search-item input::placeholder {
          color: #94a3b8;
        }

        .search-item.error {
          border: 1px solid #ff4d4f;
          box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.15);
          animation: shake 0.2s ease;
        }

        @keyframes shake {
          0% { transform: translateX(0); }
          25% { transform: translateX(-2px); }
          50% { transform: translateX(2px); }
          75% { transform: translateX(-2px); }
          100% { transform: translateX(0); }
        }

        .field-btn {
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 0;
        }

        .field-btn .field-value {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .field-btn .field-placeholder {
          color: #94a3b8;
        }

        .autocomplete-box {
          position: absolute;
          top: calc(100% + 10px);
          left: 0;
          right: 0;
          background: rgba(255,255,255,0.98);
          border: 1px solid rgba(17, 185, 163, 0.22);
          border-radius: 18px;
          box-shadow: 0 18px 35px rgba(1, 9, 18, 0.16);
          z-index: 30;
          overflow: hidden;
          backdrop-filter: blur(14px);
        }

        .autocomplete-header {
          padding: 10px 12px 8px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .3px;
          text-transform: uppercase;
          color: #0f7c76;
          background: linear-gradient(90deg, rgba(17,185,163,.10), rgba(26,127,231,.08));
          border-bottom: 1px solid rgba(17,185,163,.10);
        }

        .autocomplete-list {
          max-height: 280px;
          overflow: auto;
          padding: 6px;
        }
 
        .autocomplete-item {
          width: 100%;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          font-family: "Gotham";
          align-items: flex-start;
          gap: 10px;
          padding: 12px 12px;
          border-radius: 14px;
          text-align: left;
          transition: all .18s ease;
        }

        .autocomplete-item:hover {
          background: linear-gradient(90deg, rgba(17,185,163,.10), rgba(26,127,231,.08));
          transform: translateY(-1px);
        }

        .autocomplete-main {
          color: #17324a;
          font-size: 13px;
          font-weight: 700;
          line-height: 1.25;
        }

        .autocomplete-sub {
          color: #6b7c93;
          font-size: 11px;
          margin-top: 3px;
        }

        .empty-box {
          padding: 14px 12px;
          color: #7b8ca3;
          font-size: 12px;
        }

        .encomenda-panel {
          position: absolute;
          top: calc(100% + 10px);
          left: 0;
          right: 0;
          background: rgba(255,255,255,0.98);
          border: 1px solid rgba(17, 185, 163, 0.22);
          border-radius: 18px;
          box-shadow: 0 18px 35px rgba(1, 9, 18, 0.16);
          z-index: 30;
          overflow: hidden;
          backdrop-filter: blur(14px);
        }

        .encomenda-list {
          padding: 8px;
        }

        .encomenda-item {
          width: 100%;
          border: 1px solid transparent;
          background: linear-gradient(180deg, #ffffff, #f8fffe);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 12px 14px;
          border-radius: 14px;
          text-align: left;
          margin-bottom: 8px;
          transition: all .18s ease;
          color: #16324a;
          font-size: 13px;
          font-weight: 600;
        }

        .encomenda-item:hover {
          border-color: rgba(17,185,163,.22);
          background: linear-gradient(90deg, rgba(17,185,163,.10), rgba(26,127,231,.08));
          transform: translateY(-1px);
        }

        .encomenda-item.active {
          border-color: rgba(17,185,163,.40);
          background: linear-gradient(90deg, rgba(17,185,163,.16), rgba(26,127,231,.10));
        }

        .encomenda-badge {
          font-size: 11px;
          font-weight: 800;
          color: #0f7c76;
          background: rgba(17,185,163,.10);
          border: 1px solid rgba(17,185,163,.18);
          padding: 4px 8px;
          border-radius: 999px;
          white-space: nowrap;
        }

        .search-btn {
          background: linear-gradient(90deg, #1a7fe7, #19c4c9);
          color: #fff;
          font-size: 10px;
          font-weight: 800;
          height: 40px;
          padding: 0 25px;
          border-radius: 999px;
          border: none;
          cursor: pointer;
          white-space: nowrap;
          flex: 0 0 auto;
          z-index: 1;
          box-shadow: 0 10px 18px rgba(26, 127, 231, 0.14);
        }

        .search-btn:hover { background: #0ed079ff 100%; opacity: 0.92; }

        .panel-hint {
          margin-left: auto;
          color: #9aa7b7;
          font-size: 11px;
        }

        .collapse-label {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          cursor: text;
        }

        @media (max-width: 850px) {
          .search-bar {
            flex-direction: column;
            border-radius: 25px;
            padding: 15px;
            gap: 12px;
          }
          .search-item, .search-btn {
            width: 100%;
            flex: none;
          }
        }
      `}</style>

      <div className="search-bar">
        <div
          className={`search-item ${erroOrigem ? "error" : ""}`}
          ref={origemWrapRef}
        >
          <LocationOn style={{ color: "#11b9a3", fontSize: "20px" }} />
          <input
            type="search"
            value={origem}
            readOnly={isMobile}
            onChange={(e) => {
              setOrigem(e.target.value);
              setOrigemSelecionada(false);
              setErroOrigem(false);
              setOpenPanel("origem");
              sessionStorage.removeItem(SELECTED_SOURCE_SEARCH);
            }}
            onFocus={() => setOpenPanel("origem")}
            onClick={() => {
              if (isMobile) {
                setMobileModal("origem");
                setOpenPanel("origem");
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Origem"
            autoComplete="off"
          />

          {!isMobile && openPanel === "origem" && (
            <div className="autocomplete-box">
              <div className="autocomplete-header">Sugestões de origem</div>
              <div className="autocomplete-list">
                {loadingOrigin ? (
                  <div className="empty-box">Carregando cidades...</div>
                ) : originPredictions.length > 0 ? (
                  originPredictions.map((item) => (
                    <button
                      key={item.place_id}
                      type="button"
                      className="autocomplete-item"
                      onClick={() => selectOrigin(item)}
                    >
                      <LocationOn
                        style={{
                          color: "#11b9a3",
                          fontSize: "20px",
                          marginTop: 1,
                        }}
                      />
                      <div>
                        <div className="autocomplete-main">
                          {item.structured_formatting?.main_text ||
                            item.description.split(",")[0]}
                        </div>
                        <div className="autocomplete-sub">
                          {item.description}
                        </div>
                      </div>
                    </button>
                  ))
                ) : origem ? (
                  <div className="empty-box">Nenhuma cidade encontrada.</div>
                ) : (
                  <div className="empty-box">Digite para ver sugestões.</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div
          className={`search-item ${erroDestino ? "error" : ""}`}
          ref={destinoWrapRef}
        >
          <LocationOn style={{ color: "#11b9a3", fontSize: "20px" }} />
          <input
            type="search"
            value={destino}
            readOnly={isMobile}
            onChange={(e) => {
              setDestino(e.target.value);
              setDestinoSelecionado(false);
              setErroDestino(false);
              setOpenPanel("destino");
              sessionStorage.removeItem(SELECTED_DESTINATION_SEARCH);
            }}
            onFocus={() => setOpenPanel("destino")}
            onClick={() => {
              if (isMobile) {
                setMobileModal("destino");
                setOpenPanel("destino");
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Destino"
            autoComplete="off"
          />

          {!isMobile && openPanel === "destino" && (
            <div className="autocomplete-box">
              <div className="autocomplete-header">Sugestões de destino</div>
              <div className="autocomplete-list">
                {loadingDest ? (
                  <div className="empty-box">Carregando cidades...</div>
                ) : destPredictions.length > 0 ? (
                  destPredictions.map((item) => (
                    <button
                      key={item.place_id}
                      type="button"
                      className="autocomplete-item"
                      onClick={() => selectDestino(item)}
                    >
                      <LocationOn
                        style={{
                          color: "#11b9a3",
                          fontSize: "20px",
                          marginTop: 1,
                        }}
                      />
                      <div>
                        <div className="autocomplete-main">
                          {item.structured_formatting?.main_text ||
                            item.description.split(",")[0]}
                        </div>
                        <div className="autocomplete-sub">
                          {item.description}
                        </div>
                      </div>
                    </button>
                  ))
                ) : destino ? (
                  <div className="empty-box">Nenhuma cidade encontrada.</div>
                ) : (
                  <div className="empty-box">Digite para ver sugestões.</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div
          className={`search-item ${erroEncomenda ? "error" : ""}`}
          ref={encomendaWrapRef}
        >
          <AllInbox style={{ color: "#11b9a3", fontSize: "20px" }} />

          <button
            type="button"
            className="field-btn"
            onClick={() => {
              if (isMobile) {
                setMobileModal("encomenda");
              } else {
                setOpenPanel(openPanel === "encomenda" ? null : "encomenda");
              }
            }}
          >
            <span
              className={`field-value ${!encomenda ? "field-placeholder" : ""}`}
            >
              {encomenda || "Encomenda"}
            </span>
            <ExpandMore style={{ color: "#11b9a3", fontSize: 20 }} />
          </button>

          {!isMobile && openPanel === "encomenda" && (
            <div className="encomenda-panel">
              <div className="autocomplete-header">Selecione a encomenda</div>
              <div className="encomenda-list">
                {encomendas.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={`encomenda-item ${encomenda === item ? "active" : ""}`}
                    onClick={() => selectEncomenda(item)}
                  >
                    <span>{item}</span>
                    <span className="encomenda-badge">
                      {item === "Pequeno, estilo envelope"
                        ? "até 1kg"
                        : item === "Caixa de Sapato/Sacola"
                          ? "1 a 5kg"
                          : item === "Mochila/Mala"
                            ? "5 a 10kg"
                            : item === "Mala/Caixa"
                              ? "10 a 23kg"
                              : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          className={`search-item ${erroSeguro ? "error" : ""}`}
          onClick={() => {
            if (encomenda != "Pequeno, estilo envelope") {
              setValorSeguroInput(valorSeguro);
              setOpenSeguroModal(true);
            }
          }}
          style={{
            cursor:
              encomenda === "Pequeno, estilo envelope"
                ? "not-allowed"
                : "pointer",
            background:
              encomenda === "Pequeno, estilo envelope" ? "#f2f2f2" : "#fff",
          }}
        >
          <VerifiedUser style={{ color: "#11b9a3", fontSize: "20px" }} />
          <button
            type="button"
            className="field-btn"
            style={{
              cursor:
                encomenda === "Pequeno, estilo envelope"
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            <span
              className={`field-value ${!valorSeguro ? "field-placeholder" : ""}`}
            >
              {valorSeguro ? `${valorSeguro}` : "Seguro"}
            </span>
          </button>
        </div>

        <button
          className="search-btn"
          onClick={handleSearch}
          style={{ fontSize: 12 }}
        >
          ENVIAR ENCOMENDA
        </button>
      </div>

      {openEnvelopeWarning && (
        <Box
          onClick={() => setOpenEnvelopeWarning(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: 500,
              width: "100%",
              background: "#fff",
              borderRadius: "18px 18px 14px 14px",
              boxShadow: "0 -10px 35px rgba(0,0,0,0.25)",
              p: 2,
              border: "1px solid rgba(17,185,163,0.2)",
              animation: "slideUp .25s ease",
            }}
          >
            <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>

            <Typography
              sx={{
                fontSize: 13,
                fontFamily: "Gotham",
                color: "#516074",
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              Envio de encomendas de documentos, por não ter valor comercial,
              não terá reembolso em casos raríssimos de extravio.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={() => setOpenEnvelopeWarning(false)}
              sx={{
                borderRadius: "12px",
                fontWeight: 800,
                fontFamily: "Gotham",
                background: "linear-gradient(90deg, #1a7fe7, #19c4c9)",
                transition: "0.3s",
                boxShadow: "none",
                "&:hover": {
                  background: "#0ed079ff",
                  opacity: 0.9,
                },
              }}
            >
              Entendi
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={mobileModal !== null}
        onClose={() => {
          setMobileModal(null);
          setOpenPanel(null);
        }}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "#f6f9fc",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(90deg,#1a7fe7,#19c4c9)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontFamily: "Gotham",
              fontSize: 18,
            }}
          >
            {mobileModal === "origem" && "Cidade de origem"}
            {mobileModal === "destino" && "Cidade de destino"}
            {mobileModal === "encomenda" && "Tipo de encomenda"}
          </Typography>

          <IconButton
            onClick={() => {
              setMobileModal(null);
              setOpenPanel(null);
            }}
            sx={{ color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {mobileModal !== "encomenda" && (
            <TextField
              fullWidth
              autoFocus
              inputRef={mobileInputRef}
              placeholder="Digite o nome da cidade..."
              value={mobileModal === "origem" ? origem : destino}
              type="search"
              style={{ marginTop: 10 }}
              onChange={(e) => {
                if (mobileModal === "origem") {
                  setOrigem(e.target.value);
                  setOrigemSelecionada(false);
                  setErroOrigem(false);
                  setOpenPanel("origem");
                } else {
                  setDestino(e.target.value);
                  setDestinoSelecionado(false);
                  setErroDestino(false);
                  setOpenPanel("destino");
                }
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "18px",
                  background: "#fff",
                },
              }}
            />
          )}

          {/* ================= ORIGEM ================= */}

          {mobileModal === "origem" && (
            <>
              {loadingOrigin ? (
                <Typography align="center" sx={{ mt: 3 }}>
                  Carregando cidades...
                </Typography>
              ) : originPredictions.length > 0 ? (
                originPredictions.map((item) => (
                  <Button
                    key={item.place_id}
                    fullWidth
                    onMouseDown={() => {
                      selectOrigin(item);
                    }}
                    onClick={() => selectOrigin(item)}
                    sx={{
                      mb: 1.5,
                      p: 2,
                      justifyContent: "flex-start",
                      textTransform: "none",
                      background: "#fff",
                      borderRadius: "18px",
                      boxShadow: "0 5px 18px rgba(0,0,0,.08)",
                    }}
                  >
                    <LocationOn
                      sx={{
                        color: "#11b9a3",
                        mr: 2,
                      }}
                    />

                    <Box textAlign="left">
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#17324a",
                        }}
                      >
                        {item.structured_formatting?.main_text}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#667085",
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  </Button>
                ))
              ) : (
                <Typography align="center" sx={{ color: "#6b7280", mt: 4 }}>
                  Digite uma cidade para pesquisar.
                </Typography>
              )}
            </>
          )}

          {/* ================= DESTINO ================= */}

          {mobileModal === "destino" && (
            <>
              {loadingDest ? (
                <Typography align="center" sx={{ mt: 3 }}>
                  Carregando cidades...
                </Typography>
              ) : destPredictions.length > 0 ? (
                destPredictions.map((item) => (
                  <Button
                    key={item.place_id}
                    fullWidth
                    onMouseDown={() => {
                      selectDestino(item);
                    }}
                    onClick={() => selectDestino(item)}
                    sx={{
                      mb: 1.5,
                      p: 2,
                      justifyContent: "flex-start",
                      textTransform: "none",
                      background: "#fff",
                      borderRadius: "18px",
                      boxShadow: "0 5px 18px rgba(0,0,0,.08)",
                    }}
                  >
                    <LocationOn
                      sx={{
                        color: "#11b9a3",
                        mr: 2,
                      }}
                    />

                    <Box textAlign="left">
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#17324a",
                        }}
                      >
                        {item.structured_formatting?.main_text}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#667085",
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  </Button>
                ))
              ) : (
                <Typography align="center" sx={{ color: "#6b7280", mt: 4 }}>
                  Digite uma cidade para pesquisar.
                </Typography>
              )}
            </>
          )}

          {/* ================= ENCOMENDAS ================= */}

          {mobileModal === "encomenda" && (
            <Box style={{ marginTop: 10 }}>
              {encomendas.map((item) => (
                <Button
                  key={item}
                  fullWidth
                  onClick={() => {
                    selectEncomenda(item);
                    setMobileModal(null);
                  }}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: "18px",
                    justifyContent: "space-between",
                    textTransform: "none",
                    background:
                      encomenda === item ? "rgba(17,185,163,.12)" : "#fff",
                    border:
                      encomenda === item
                        ? "2px solid #11b9a3"
                        : "1px solid #e5e7eb",
                    boxShadow: "0 5px 18px rgba(0,0,0,.06)",
                  }}
                >
                  <Box textAlign="left">
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontFamily: "Gotham",
                        color: "#17324a",
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontFamily: "Gotham",
                      color: "#11b9a3",
                      fontSize: 12,
                    }}
                  >
                    {item === "Pequeno, estilo envelope"
                      ? "até 1kg"
                      : item === "Caixa de Sapato/Sacola"
                        ? "1 a 5kg"
                        : item === "Mochila/Mala"
                          ? "5 a 10kg"
                          : item === "Mala/Caixa"
                            ? "10 a 23kg"
                            : ""}
                  </Typography>
                </Button>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openSeguroModal}
        onClose={() => setOpenSeguroModal(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            border: "1px solid rgba(17,185,163,0.14)",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.45)",
            backdropFilter: "blur(6px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            background: "linear-gradient(90deg, #1a7fe7, #19c4c9)",
            color: "#fff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "14px",
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <VerifiedUser style={{ fontSize: 22, color: "#fff" }} />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontFamily: "Gotham",
                    fontWeight: 800,
                    lineHeight: 1.1,
                  }}
                >
                  O que é o Valor do Seguro
                </Typography>
                <Typography
                  sx={{ fontSize: 12.5, fontFamily: "Gotham", opacity: 0.9 }}
                >
                  Proteção extra com valor definido por você
                </Typography>
              </Box>
            </Box>

            <IconButton
              onClick={() => setOpenSeguroModal(false)}
              sx={{ color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontFamily: "Gotham",
              color: "#4b5563",
              lineHeight: 1.6,
              marginTop: 2,
              mb: 2.5,
            }}
          >
            O valor do seguro é um valor que você atribui a sua encomenda e que
            receberá em caso de extravio ou danificações. Como este valor será
            reservado do cartão de crédito do viajante "carteiro", aconselhamos
            ser justo a fim de não inviabilizar o interesse dele em levar.
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: "18px",
              background: "linear-gradient(180deg, #f8fffe 0%, #ffffff 100%)",
              border: "1px solid rgba(17,185,163,0.14)",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: "Gotham",
                fontWeight: 700,
                color: "#0f7c76",
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: ".4px",
              }}
            >
              Valor do seguro
            </Typography>

            <TextField
              fullWidth
              value={valorSeguroInput}
              onChange={(e) => {
                const formatted = formatarMoeda(e.target.value);
                setValorSeguroInput(formatted);
              }}
              placeholder="R$ 0,00"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "#fff",
                  color: "#555",
                  boxShadow: "5px 5px 15px rgba(0, 0, 0, 0.1)",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
            gap: 1.5,
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => setOpenSeguroModal(false)}
            variant="outlined"
            sx={{
              flex: 1,
              height: 46,
              fontWeight: 700,
              background: "#e8e8e8",
              borderRadius: "999px",
              textTransform: "none",
              border: "none",
              color: "#10233d",
              "&:hover": {
                background: "#d9d9d9",
              },
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={() => {
              setValorSeguro(valorSeguroInput);
              setErroSeguro(false);
              setOpenSeguroModal(false);
            }}
            variant="contained"
            sx={{
              flex: 1,
              height: 46,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 800,
              boxShadow: "none",
              background: "linear-gradient(90deg, #1a7fe7, #19c4c9)",
              "&:hover": {
                background: "#0ed079ff 100%",
                boxShadow: "none",
              },
            }}
          >
            Assegurar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
